/*
  Правка сведений о субъекте права - §6.4.2, §7.4.1.

  Заведено 17.09.2026, строка 85 реестра компромиссов. До этого дня экран
  должников только ЧИТАЛ: паспорт, ИНН и СНИЛС показывались словом «не указан», а
  внести их было нечем ни здесь, ни где-либо ещё. Заявление при этом уходило с
  ходатайством об истребовании идентификаторов - даже по должнику, чей паспорт у
  организации есть.

  Отдельным файлом от `AccountDebtors.jsx`: там список и то, как он читается,
  здесь форма и то, как она отправляется. Вместе это две ответственности в одном
  компоненте.

  Что здесь правится и что нет. Правятся сведения о ЧЕЛОВЕКЕ. Роль, вид
  ответственности и доля описывают не его, а его связь с лицевым счётом, и живут
  отдельно: у одного человека бывает несколько счетов с разными ролями.

  Проверки идентификаторов делает сервер и только он: контрольные разряды ИНН и
  СНИЛС - это правило, у которого одно место, а вторая его копия на клиенте
  разошлась бы с первой молча.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { Field, Notice } from './Common.jsx'
import Modal from './Modal.jsx'

/*
  Поля формы одним перечнем: подпись, ключ, ширина и образец значения.

  Не девять кусков разметки подряд - у них одинаковое поведение, и переписывать
  его девять раз значит получить девять слегка разных полей. Ширина - по длине
  ожидаемого значения (`s` для даты и номера, `m` для имени и почты).

  🔴 Пояснений под полями здесь нет намеренно, и это не экономия. Пояснение
  удлиняет своё поле снизу, а строка полей выровнена по нижнему краю - два поля
  с пояснением из девяти подняли остальные ступеньками (осмотр 17.09.2026).
  Что нужно сказать о формате, говорит образец значения в самом поле; что нужно
  сказать о смысле - общий абзац под формой.
*/
const FIELDS = [
  { key: 'last_name', label: 'Фамилия', width: 'm' },
  { key: 'first_name', label: 'Имя', width: 'm' },
  { key: 'middle_name', label: 'Отчество', width: 'm' },
  { key: 'birth_date', label: 'Дата рождения', width: 's', type: 'date' },
  /* Место рождения стоит сразу за датой (30.09.2026): в заявлении они идут
     рядом и указываются, если известны взыскателю. Поле широкое - в бланках
     это целая строка вида «д.Юрлово, Солнечногорский р-он, Московская обл.». */
  {
    key: 'birth_place',
    label: 'Место рождения',
    width: 'm',
    placeholder: 'д. Юрлово, Солнечногорский р-он, Московская обл.',
  },
  { key: 'inn', label: 'ИНН', width: 's', placeholder: '123456789012' },
  { key: 'snils', label: 'СНИЛС', width: 's', placeholder: '112-233-445 95' },
  { key: 'passport', label: 'Паспорт', width: 's', placeholder: '4506 123456' },
  { key: 'phone', label: 'Телефон', width: 's', placeholder: '+7 900 000-00-00' },
  { key: 'email', label: 'Почта', width: 'm', placeholder: 'ivanov@example.ru' },
]

function valuesOf(debtor) {
  /*
    Пустое значение приходит с сервера как `null`, а поле ввода требует строку:
    без замены React считает поле неуправляемым и ругается в консоли, а при
    первом наборе теряет начало ввода.
  */
  const values = {}
  FIELDS.forEach(({ key }) => {
    values[key] = debtor[key] ?? ''
  })
  return values
}

export default function DebtorForm({ debtor, onSaved, onCancel }) {
  const [values, setValues] = useState(() => valuesOf(debtor))
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const save = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.savePerson(debtor.person_id, values)
      onSaved()
    } catch (failure) {
      // Отказ остаётся ВНУТРИ окна: за его пределами человек его не увидит и
      // решит, что ничего не произошло. Текст приходит с сервера целиком - и
      // что случилось, и что делать сейчас.
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={`Сведения о должнике: ${debtor.full_name}`} onClose={onCancel} busy={busy}>
      <form onSubmit={save}>
        <div className="row row--fields">
          {FIELDS.map(({ key, label, width, type, placeholder }) => (
            <Field key={key} label={label} width={width}>
              <input
                className="field__input"
                type={type ?? 'text'}
                value={values[key]}
                placeholder={placeholder}
                disabled={busy}
                onChange={(event) => setValues({ ...values, [key]: event.target.value })}
              />
            </Field>
          ))}
        </div>

        <div className="muted">
          Эти сведения печатаются в заявлении о вынесении судебного приказа. Достаточно
          одного идентификатора из трёх: пустое поле означает «неизвестен», суд запросит
          его сам, и подаче это не препятствует (пункт 3 части 2 статьи 124 ГПК РФ).
          Паспорт хранится в зашифрованном виде, поэтому поиск по нему невозможен.
        </div>

        <Notice error={error} />

        {/* Кнопки отдельной строкой, а не в строке с последним полем: поле выше
            кнопки на высоту подписи, и в одной строке они встают вразнобой. */}
        <div className="row row--tight">
          <button className="button" type="submit" disabled={busy}>
            {busy ? 'Сохраняем…' : 'Сохранить сведения'}
          </button>
          <button className="button button--quiet" type="button" disabled={busy} onClick={onCancel}>
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  )
}
